<template>
  <div class="company-audit-log">
    <b-alert
      v-if="errorMessage"
      show
      variant="warning"
      dismissible
      class="audit-alert mb-4"
      @dismissed="errorMessage = null"
    >
      {{ errorMessage }}
    </b-alert>

    <b-card class="audit-filters-card border-0 shadow-sm mb-4" body-class="p-4">
      <div
        class="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-md-between mb-4 w-100"
      >
        <div class="mb-3 mb-md-0">
          <h6 class="font-weight-bold mb-1">Filters</h6>
          <p class="text-muted small mb-0">
            Search updates as you type; use Apply for an immediate refresh.
          </p>
        </div>
        <div class="audit-toolbar-btns">
          <b-button
            variant="primary"
            class="audit-toolbar-btn"
            @click="applyFiltersNow"
          >
            Apply
          </b-button>
          <b-button
            variant="outline-secondary"
            class="audit-toolbar-btn"
            @click="clearFilters"
          >
            <span class="d-sm-none">Clear</span>
            <span class="d-none d-sm-inline">Clear all</span>
          </b-button>
        </div>
      </div>

      <b-row class="audit-filter-row">
        <b-col cols="12" sm="6" lg="3" class="mb-3">
          <label class="audit-label">Event</label>
          <b-form-input
            v-model="filters.event"
            :placeholder="isNarrow ? 'e.g. Login' : 'e.g. Login, Upload'"
            class="audit-input"
            @keydown.enter.prevent="applyFiltersNow"
          />
        </b-col>
        <b-col cols="12" sm="6" lg="3" class="mb-3">
          <label class="audit-label">Participant</label>
          <b-form-input
            v-model="filters.participant"
            placeholder="Name or email"
            class="audit-input"
            @keydown.enter.prevent="applyFiltersNow"
          />
        </b-col>
        <b-col cols="12" sm="6" lg="3" class="mb-3">
          <label class="audit-label">From</label>
          <b-form-datepicker
            v-model="filters.dateFrom"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            :max="dateFromInputMax"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            placeholder="dd/mm/yyyy"
            class="audit-input date-input"
            @input="onDateFromChange"
          />
        </b-col>
        <b-col cols="12" sm="6" lg="3" class="mb-3">
          <label class="audit-label">To</label>
          <b-form-datepicker
            v-model="filters.dateTo"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            :min="dateToInputMin"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            placeholder="dd/mm/yyyy"
            class="audit-input date-input"
            @input="onDateToChange"
          />
        </b-col>
      </b-row>

      <b-row class="audit-filter-row align-items-end">
        <b-col cols="12" sm="6" lg="4" class="mb-3 mb-lg-0">
          <label class="audit-label">Status</label>
          <b-form-select
            v-model="filters.status"
            class="audit-input"
            :options="statusOptions"
            @change="reloadFirstPage"
          />
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mb-0">
          <label class="audit-label">Sort</label>
          <b-form-select
            v-model="sort"
            class="audit-input"
            :options="sortOptions"
            @change="reloadFirstPage"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-card
      class="audit-table-card border-0 shadow-sm overflow-hidden"
      body-class="p-0"
    >
      <div
        class="audit-results-bar px-3 py-2 border-bottom d-flex flex-wrap align-items-center justify-content-between"
      >
        <span class="small font-weight-bold text-secondary">Activity</span>
        <span v-if="!loading && meta" class="small text-muted">
          {{ resultRangeText }}
        </span>
      </div>

      <!-- Mobile (< 768 px): custom full-width card list — avoids b-table's
           stacked mode which doesn't fill container width reliably at 320 px. -->
      <div v-if="windowWidth < 768" class="audit-cards">
        <div v-if="loading" class="text-center text-primary py-4">
          <digger-loader />
        </div>
        <div v-else-if="items.length === 0" class="audit-empty py-5 px-3">
          <div class="audit-empty-inner">
            <div class="audit-empty-icon" aria-hidden="true">
              <b-icon icon="inbox" font-scale="2" class="text-muted" />
            </div>
            <p class="font-weight-bold mb-1">No entries found</p>
            <p class="text-muted small mb-0">
              Try widening your filters or clearing them to see more activity.
            </p>
          </div>
        </div>
        <div
          v-for="item in items"
          v-else
          :key="item.id"
          class="audit-card"
          :class="{ 'audit-card--failed': normalizedStatus(item) === 'failed' }"
          @click="openDetail(item)"
        >
          <div class="audit-card__row">
            <span class="audit-card__label">Date</span>
            <span class="audit-card__value">
              {{ formatDateTime(item.created_at) }}
              <span class="audit-card__rel text-muted">{{
                relativeTime(item.created_at)
              }}</span>
            </span>
          </div>
          <div class="audit-card__row">
            <span class="audit-card__label">Event</span>
            <span class="audit-card__value text-break">{{
              item.event || '-'
            }}</span>
          </div>
          <div class="audit-card__row">
            <span class="audit-card__label">Status</span>
            <span class="audit-card__value">
              <b-badge
                v-if="normalizedStatus(item)"
                :variant="statusBadgeVariant(item)"
              >
                {{ normalizedStatus(item) }}
              </b-badge>
              <span v-else class="text-muted">-</span>
            </span>
          </div>
          <b-button
            size="sm"
            variant="outline-primary"
            class="audit-details-btn mt-2"
            @click.stop="openDetail(item)"
          >
            <b-icon icon="eye" class="mr-1" />
            View
          </b-button>
        </div>
      </div>

      <!-- Desktop (>= 768 px): standard table layout. -->
      <b-table
        v-else
        striped
        hover
        show-empty
        head-variant="light"
        class="audit-table audit-table-fixed mb-0"
        :fields="fields"
        :items="items"
        :busy="loading"
        :tbody-tr-class="rowClass"
        @row-clicked="openDetail"
      >
        <template #table-busy>
          <div class="text-center text-primary my-4">
            <digger-loader />
          </div>
        </template>

        <template #empty>
          <div v-if="!loading" class="audit-empty py-5 px-3">
            <div class="audit-empty-inner">
              <div class="audit-empty-icon" aria-hidden="true">
                <b-icon icon="inbox" font-scale="2" class="text-muted" />
              </div>
              <p class="font-weight-bold mb-1">No entries found</p>
              <p class="text-muted small mb-0">
                Try widening your filters or clearing them to see more activity.
              </p>
            </div>
          </div>
        </template>

        <template #cell(created_at)="{ item }">
          <div class="audit-cell-time">
            <div>{{ formatDateTime(item.created_at) }}</div>
            <div class="small text-muted">
              {{ relativeTime(item.created_at) }}
            </div>
          </div>
        </template>

        <template #cell(event)="{ item }">
          <span class="text-break">{{ item.event || '-' }}</span>
        </template>

        <template #cell(description)="{ item }">
          <span
            :title="item.description"
            class="description-preview d-inline-block text-break"
          >
            {{ item.description || '-' }}
          </span>
        </template>

        <template #cell(status)="{ item }">
          <b-badge
            v-if="normalizedStatus(item)"
            :variant="statusBadgeVariant(item)"
          >
            {{ normalizedStatus(item) }}
          </b-badge>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(causer_label)="{ item }">
          {{ formatActor(item.causer) }}
        </template>

        <template #cell(target_label)="{ item }">
          {{
            item.target && (item.target.name || item.target.email)
              ? formatActor(item.target)
              : item.item_label || formatActor(item.target)
          }}
        </template>

        <template #cell(actions)="{ item }">
          <b-button
            size="sm"
            variant="outline-primary"
            class="audit-details-btn"
            @click.stop="openDetail(item)"
          >
            <b-icon icon="eye" class="mr-1" />
            View
          </b-button>
        </template>
      </b-table>

      <div
        v-if="totalRows > perPage"
        class="audit-pagination-wrap border-top bg-light px-2 py-3"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="center"
          class="mb-0"
          hide-goto-end-buttons
        />
      </div>
    </b-card>

    <b-modal
      v-model="detailOpen"
      ok-only
      ok-title="Close"
      ok-variant="primary"
      size="lg"
      centered
      body-class="pt-0"
      @hidden="selected = null"
    >
      <template #modal-title>
        <span class="h5 font-weight-bold mb-0">Audit entry</span>
      </template>
      <template v-if="selected">
        <dl class="row mb-0 audit-detail-dl">
          <dt class="col-sm-3">When</dt>
          <dd class="col-sm-9">{{ formatDateTime(selected.created_at) }}</dd>

          <dt class="col-sm-3">Event</dt>
          <dd class="col-sm-9">{{ selected.event || '-' }}</dd>

          <dt class="col-sm-3">Description</dt>
          <dd class="col-sm-9 text-break">{{ selected.description || '-' }}</dd>

          <dt class="col-sm-3">Status</dt>
          <dd class="col-sm-9">
            <b-badge
              v-if="normalizedStatus(selected)"
              :variant="statusBadgeVariant(selected)"
            >
              {{ normalizedStatus(selected) }}
            </b-badge>
            <span v-else>-</span>
          </dd>

          <dt class="col-sm-3">Causer</dt>
          <dd class="col-sm-9">{{ formatActor(selected.causer) }}</dd>

          <dt class="col-sm-3">Target</dt>
          <dd class="col-sm-9">
            {{
              selected.target && (selected.target.name || selected.target.email)
                ? formatActor(selected.target)
                : selected.item_label || formatActor(selected.target)
            }}
          </dd>

          <dt class="col-sm-3">Company</dt>
          <dd class="col-sm-9">
            <template v-if="companyLabel(selected)">{{
              companyLabel(selected)
            }}</template>
            <span v-else class="text-muted">-</span>
          </dd>

          <dt class="col-sm-3">User type</dt>
          <dd class="col-sm-9">{{ selected.user_type || '-' }}</dd>

          <dt class="col-sm-3">Target user type</dt>
          <dd class="col-sm-9">{{ selected.target_user_type || '-' }}</dd>

          <dt class="col-sm-3">Subject</dt>
          <dd class="col-sm-9 text-break">
            <template v-if="selected.subject_type || selected.subject_id">
              {{ selected.subject_type || '' }}
              {{ selected.subject_id != null ? `#${selected.subject_id}` : '' }}
            </template>
            <span v-else class="text-muted">-</span>
          </dd>

          <dt class="col-sm-3">IP / Source IP</dt>
          <dd class="col-sm-9">
            {{ selected.ip_address || selected.source_ip || '-' }}
            <template
              v-if="
                selected.source_ip &&
                selected.ip_address &&
                selected.source_ip !== selected.ip_address
              "
            >
              <span class="text-muted">
                (source: {{ selected.source_ip }})</span
              >
            </template>
          </dd>

          <dt class="col-sm-3">Client</dt>
          <dd class="col-sm-9">{{ selected.client_type || '-' }}</dd>

          <dt class="col-sm-3">User agent</dt>
          <dd class="col-sm-9 text-break small text-muted">
            {{ selected.user_agent || '-' }}
          </dd>

          <dt v-if="selected.failure_reason" class="col-sm-3">
            Failure reason
          </dt>
          <dd
            v-if="selected.failure_reason"
            class="col-sm-9 text-break text-danger"
          >
            {{ selected.failure_reason }}
          </dd>
        </dl>
      </template>
    </b-modal>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { mapGetters } from 'vuex'

dayjs.extend(relativeTime)

const DEFAULT_PER_PAGE = 10

export default {
  name: 'CompanyAuditLogList',

  data() {
    return {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,

      items: [],
      meta: null,
      loading: false,
      errorMessage: null,

      currentPage: 1,
      perPage: DEFAULT_PER_PAGE,
      sort: '-created_at',

      /** en-AU default renders "28 July 2026"; force numeric dd/mm/yyyy. */
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },

      filters: {
        event: '',
        participant: '',
        dateFrom: '',
        dateTo: '',
        status: '',
      },

      detailOpen: false,
      selected: null,

      statusOptions: [
        { value: '', text: 'All' },
        { value: 'success', text: 'Success' },
        { value: 'failed', text: 'Failed' },
      ],

      sortOptions: [
        { value: '-created_at', text: 'Newest first' },
        { value: 'created_at', text: 'Oldest first' },
        { value: 'event', text: 'Event (A–Z)' },
        { value: '-event', text: 'Event (Z–A)' },
        { value: 'description', text: 'Description (A–Z)' },
        { value: '-description', text: 'Description (Z–A)' },
      ],
    }
  },

  computed: {
    ...mapGetters('auth', ['user']),

    /** Phone-width: used to swap in shorter placeholders/labels that fit. */
    isNarrow() {
      return this.windowWidth < 576
    },

    /**
     * Responsive columns. Below md the table renders in stacked "card" mode
     * (see `stacked="md"`), so we show only the key fields to keep each card
     * clean and scannable — full details stay reachable via the row's View
     * button. At >=md it's a normal table with columns revealed progressively
     * (Who at lg, Target at xl), matching the current desktop layout at >=1200px.
     */
    fields() {
      if (this.windowWidth < 768) {
        return [
          { key: 'created_at', label: 'Date' },
          { key: 'event', label: 'Event' },
          { key: 'status', label: 'Status' },
          { key: 'actions', label: 'Details' },
        ]
      }

      const atLg = ' d-none d-lg-table-cell'
      const atXl = ' d-none d-xl-table-cell'
      return [
        {
          key: 'created_at',
          label: 'Date',
          thClass: 'audit-col-date',
          tdClass: 'align-top audit-col-date',
        },
        {
          key: 'event',
          label: 'Event',
          thClass: 'audit-col-event',
          tdClass: 'align-top audit-col-event',
        },
        {
          key: 'description',
          label: 'Description',
          thClass: 'audit-col-description',
          tdClass: 'align-top audit-col-description',
        },
        {
          key: 'status',
          label: 'Status',
          thClass: 'audit-col-status',
          tdClass: 'align-top text-nowrap audit-col-status',
        },
        {
          key: 'causer_label',
          label: 'Who',
          thClass: 'audit-col-who' + atLg,
          tdClass: 'align-top audit-col-who' + atLg,
        },
        {
          key: 'target_label',
          label: 'Target',
          thClass: 'audit-col-target' + atXl,
          tdClass: 'align-top audit-col-target' + atXl,
        },
        {
          key: 'actions',
          label: '',
          thClass: 'audit-col-actions',
          tdClass: 'align-top text-nowrap audit-col-actions',
        },
      ]
    },

    totalRows() {
      if (this.meta && typeof this.meta.total === 'number')
        return this.meta.total
      return 0
    },

    resultRangeText() {
      const m = this.meta
      if (!m || typeof m.total !== 'number') return ''
      if (m.total === 0) return 'No entries'
      const from = m.from
      const to = m.to
      if (from != null && to != null) {
        return `Showing ${from}–${to} of ${m.total}`
      }
      return `${m.total} ${m.total === 1 ? 'entry' : 'entries'}`
    },

    /** Upper bound for "From" when "To" is set (YYYY-MM-DD compares lexicographically). */
    dateFromInputMax() {
      return this.filters.dateTo || undefined
    },

    /** Lower bound for "To" when "From" is set. */
    dateToInputMin() {
      return this.filters.dateFrom || undefined
    },
  },

  watch: {
    currentPage() {
      this.loadLogs()
    },
    'filters.event'() {
      this.scheduleFilterReload()
    },
    'filters.participant'() {
      this.scheduleFilterReload()
    },
  },

  mounted() {
    window.addEventListener('resize', this.onResize)
    if (this.user && this.user.user_type === 'business_admin') {
      this.loadLogs()
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    if (this._filterDebounce) clearTimeout(this._filterDebounce)
  },

  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },

    rowClass(item, type) {
      if (!item || type !== 'row') return ''
      const s = this.normalizedStatus(item)
      if (s === 'failed') return 'audit-row-failed'
      return ''
    },

    scheduleFilterReload() {
      if (this._filterDebounce) clearTimeout(this._filterDebounce)
      this._filterDebounce = setTimeout(() => {
        this._filterDebounce = null
        this.reloadFirstPage()
      }, 400)
    },

    /** Clears debounce and refetches immediately (e.g. Enter in a filter field). */
    applyFiltersNow() {
      if (this._filterDebounce) clearTimeout(this._filterDebounce)
      this._filterDebounce = null
      this.reloadFirstPage()
    },

    reloadFirstPage() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
        return
      }
      this.loadLogs()
    },

    onDateFromChange() {
      const from = this.filters.dateFrom
      const to = this.filters.dateTo
      if (from && to && from > to) {
        this.filters.dateTo = from
      }
      this.reloadFirstPage()
    },

    onDateToChange() {
      const from = this.filters.dateFrom
      const to = this.filters.dateTo
      if (from && to && to < from) {
        this.filters.dateFrom = to
      }
      this.reloadFirstPage()
    },

    clearFilters() {
      this.filters = {
        event: '',
        participant: '',
        dateFrom: '',
        dateTo: '',
        status: '',
      }
      this.sort = '-created_at'
      this.reloadFirstPage()
    },

    buildParams() {
      const params = {
        page: this.currentPage,
        limit: this.perPage,
        sort: this.sort,
      }
      const e = (this.filters.event || '').trim()
      const p = (this.filters.participant || '').trim()
      if (e) params['filter[event]'] = e
      if (p) params['filter[participant]'] = p
      let df = this.filters.dateFrom
      let dt = this.filters.dateTo
      if (df && dt && df > dt) {
        const t = df
        df = dt
        dt = t
      }
      if (df) params['filter[date_from]'] = df
      if (dt) params['filter[date_to]'] = dt
      if (this.filters.status) params['filter[status]'] = this.filters.status
      return params
    },

    async loadLogs() {
      if (!this.user || this.user.user_type !== 'business_admin') return

      this.loading = true
      this.errorMessage = null
      try {
        const { data } = await this.$api.get('audit-logs', {
          params: this.buildParams(),
        })

        const rows = Array.isArray(data.data) ? data.data : []
        this.items = rows
        this.meta = data.meta || null
      } catch (e) {
        this.items = []
        this.meta = null
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          e.message ||
          'Could not load audit logs.'
        this.errorMessage = msg
      } finally {
        this.loading = false
      }
    },

    formatDateTime(iso) {
      if (!iso) return '-'
      return dayjs(iso).format('D MMM YYYY, h:mm a')
    },

    relativeTime(iso) {
      if (!iso) return ''
      return dayjs(iso).fromNow()
    },

    normalizedStatus(item) {
      if (!item || item.status == null || item.status === '') return ''
      const s = String(item.status).toLowerCase()
      if (s === 'fail') return 'failed'
      if (s === 'error' || s === 'failed') return 'failed'
      if (s === 'success') return 'success'
      return String(item.status)
    },

    statusBadgeVariant(item) {
      const s = this.normalizedStatus(item)
      if (s === 'success') return 'success'
      if (s === 'failed') return 'danger'
      return 'secondary'
    },

    formatActor(actor) {
      if (!actor || typeof actor !== 'object') return '-'
      const name = (actor.name || '').trim()
      const email = (actor.email || '').trim()
      if (name && email) return `${name} (${email})`
      if (name) return name
      if (email) return email
      return '-'
    },

    companyLabel(item) {
      const c = item && item.company
      if (!c || typeof c !== 'object') return ''
      const id = c.id
      const name = (c.name || '').trim()
      if (name && id != null) return `${name} (#${id})`
      if (name) return name
      if (id != null) return `#${id}`
      return ''
    },

    openDetail(item) {
      this.selected = item
      this.detailOpen = true
    },
  },
}
</script>

<style scoped>
.company-audit-log {
  max-width: 100%;
}

.audit-alert {
  border-radius: 0.375rem;
}

.audit-filters-card {
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.audit-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0.35rem;
}

.audit-input {
  border-radius: 0.375rem;
}

/* b-form-datepicker's own .b-form-btn-label-control.form-control rule (two
   classes) beats the app-wide `.form-control { height: 50px }` rule (one class),
   so the From/To pickers render shorter than the sibling filter controls despite
   size="sm". Match them back to 50px. */
.company-audit-log >>> .date-input.form-control {
  height: 50px;
}

.audit-filter-row:last-child {
  margin-bottom: 0 !important;
}

.audit-toolbar-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.audit-toolbar-btn {
  min-height: 38px;
  padding-left: 1.15rem;
  padding-right: 1.15rem;
  font-weight: 600;
  border-radius: 0.375rem;
}

.audit-table-card {
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.audit-results-bar {
  background: #f8f9fa !important;
}

.audit-pagination-wrap >>> .pagination {
  margin-bottom: 0;
}

/*
  Description was cramped mainly from max-width: 320px on the text.
  Give the column a large share of width; other cols keep minimums via %.
*/
.audit-table-fixed {
  table-layout: fixed;
  width: 100%;
}

.audit-table-fixed >>> th.audit-col-date,
.audit-table-fixed >>> td.audit-col-date {
  width: 13%;
  min-width: 6.5rem;
}

.audit-table-fixed >>> th.audit-col-event,
.audit-table-fixed >>> td.audit-col-event {
  width: 16%;
  min-width: 7rem;
}

.audit-table-fixed >>> th.audit-col-description,
.audit-table-fixed >>> td.audit-col-description {
  width: 28%;
  min-width: 10rem;
}

.audit-table-fixed >>> th.audit-col-status,
.audit-table-fixed >>> td.audit-col-status {
  width: 7%;
  min-width: 4.5rem;
}

.audit-table-fixed >>> th.audit-col-who,
.audit-table-fixed >>> td.audit-col-who {
  width: 14%;
  min-width: 7.5rem;
}

.audit-table-fixed >>> th.audit-col-target,
.audit-table-fixed >>> td.audit-col-target {
  width: 10%;
  min-width: 5.5rem;
}

.audit-table-fixed >>> th.audit-col-actions,
.audit-table-fixed >>> td.audit-col-actions {
  width: 12%;
  min-width: 6rem;
}

/* Push the View button to the right of its column so it fills the space
   instead of leaving a gap on the right. */
.audit-table-fixed >>> td.audit-col-actions {
  text-align: right;
}

/* Who/Target hold "name (email)" — long unbreakable strings that would overflow
   a fixed-width column and overlap the next one. Let them wrap inside the cell. */
.audit-table-fixed >>> td.audit-col-who,
.audit-table-fixed >>> td.audit-col-target {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.description-preview {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
}

.audit-table >>> thead th {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #495057;
  border-bottom-width: 2px;
  white-space: nowrap;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.audit-table >>> tbody tr {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.audit-table >>> tbody td {
  vertical-align: middle;
  padding: 0.7rem 0.5rem;
  font-size: 0.82rem;
}

.audit-details-btn {
  border-radius: 0.375rem;
  font-weight: 600;
  white-space: nowrap;
  font-size: 0.75rem;
  padding: 0.2rem 0.55rem;
}

.audit-row-failed {
  background-color: rgba(220, 53, 69, 0.06) !important;
}

.audit-detail-dl dt {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.875rem;
}

.audit-detail-dl dd {
  margin-bottom: 0.75rem;
}

/* Empty slot renders in a colspan <td>; browsers often left-align it */
.audit-table >>> tbody td[colspan] {
  text-align: center !important;
  vertical-align: middle !important;
}

.audit-empty {
  width: 100%;
  display: flex;
  justify-content: center;
}

.audit-empty-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 22rem;
}

.audit-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 0.5rem;
}

.audit-empty-icon .b-icon {
  display: block;
}

.audit-cell-time {
  min-width: 140px;
}

/* Below xl: fewer columns show (priority columns), so drop the fixed percentage
   widths/min-widths and let the visible columns size to content. Keeps the table
   on the page without a horizontal scroll. At >=1200px the full fixed-width
   layout above (the current desktop UI) applies. */
@media (max-width: 1199.98px) {
  .audit-table-fixed {
    table-layout: auto;
  }
  .audit-table-fixed >>> th,
  .audit-table-fixed >>> td {
    width: auto !important;
    min-width: 0 !important;
  }
  /* Long emails/values must wrap so the auto-layout table can't exceed the card. */
  .audit-table-fixed >>> td {
    word-break: break-word;
    overflow-wrap: anywhere;
  }
}

/* Phones/tablets (below md): custom card list replaces b-table entirely.
   Each entry is a plain div card that always fills the container width. */
@media (max-width: 767.98px) {
  .company-audit-log {
    width: 100%;
  }
  .audit-cards {
    padding: 0.5rem 0.6rem 0.6rem;
  }
  .audit-card {
    width: 100%;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 0.65rem 0.9rem;
    margin-bottom: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    cursor: pointer;
  }
  .audit-card--failed {
    background-color: rgba(220, 53, 69, 0.06);
    border-color: rgba(220, 53, 69, 0.2);
  }
  .audit-card__row {
    display: flex;
    flex-direction: column;
    padding: 0.25rem 0;
  }
  .audit-card__label {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6c757d;
    margin-bottom: 0.1rem;
  }
  .audit-card__value {
    font-size: 0.85rem;
    word-break: break-word;
  }
  .audit-card__rel {
    display: block;
    font-size: 0.75rem;
  }
  .audit-details-btn {
    width: 100%;
    font-size: 0.82rem;
    padding: 0.3rem 0.75rem;
    justify-content: center;
  }
}

/* Below md the filter header stacks; lay the Apply / Clear all buttons out as
   a full-width, equal 2-column grid so they never collapse or clip their text. */
@media (max-width: 767.98px) {
  .audit-toolbar-btns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    width: 100% !important;
  }

  .audit-toolbar-btn {
    width: 100%;
    min-width: 0;
    white-space: nowrap;
    font-size: 0.85rem;
    padding-left: 0.4rem;
    padding-right: 0.4rem;
  }
}

/* Phones: reclaim width by trimming the card's heavy padding, and shrink the
   filter inputs' text so their placeholders fit instead of clipping. */
@media (max-width: 575.98px) {
  .audit-filters-card >>> .card-body {
    padding: 1rem !important;
  }
  .audit-input {
    font-size: 0.85rem;
  }
  .audit-input::placeholder {
    font-size: 0.85rem;
  }

  /* The filter row's negative gutter margins push the inputs out to the card
     edges (touching), unlike the inset header. Zero them out (single column at
     this width, so no inter-column gutter is needed) so the inputs line up with
     the header and keep the card's padding on both sides. */
  .audit-filter-row {
    margin-left: 0;
    margin-right: 0;
  }
  .audit-filter-row > [class*='col'] {
    padding-left: 0;
    padding-right: 0;
  }
}

/* Guard against any horizontal overflow shifting the filters card off-centre. */
.company-audit-log {
  overflow-x: hidden;
}
</style>
